import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Check,
  Download,
  MoreVertical,
  Pencil,
  Send,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { InvoiceStatusBadge } from '@/components/shared/StatusBadges'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { LoadingOverlay } from '@/components/shared'
import { InvoiceSendDialog } from '@/components/billing/InvoiceSendDialog'
import { CreateCreditNoteDialog } from '@/components/billing/CreateCreditNoteDialog'
import { DeleteCreditNoteDialog } from '@/components/billing/DeleteCreditNoteDialog'
import { SendCreditNoteDialog } from '@/components/billing/SendCreditNoteDialog'
import { DeleteInvoiceDialog } from '@/components/billing/DeleteInvoiceDialog'
import type { InvoiceLifecycleStep } from '@/components/billing/InvoiceLifecycleCard'
import { InvoiceValidateDialog } from '@/components/billing/InvoiceValidateDialog'
import { InvoiceDetailsTab } from '@/components/invoices/tabs/InvoiceDetailsTab'
import { InvoiceCreditNotesTab } from '@/components/invoices/tabs/InvoiceCreditNotesTab'
import { InvoicePaymentsTab } from '@/components/invoices/tabs/InvoicePaymentsTab'
import { InvoiceHistoryTab } from '@/components/invoices/tabs/InvoiceHistoryTab'
import { PaymentDrawerShell } from '@/components/payments/PaymentDrawerShell'
import { PaymentDetailDrawerContent } from '@/components/payments/PaymentDetailDrawerContent'
import {
  INVOICE_DETAIL_TAB_STALE_TIME,
  INVOICE_HISTORY_QUERY,
  creditNotesListQueryOptions,
  invoiceHistoryQueryOptions,
  useInvoice,
  useCreditNotesList,
  useUpdateInvoiceNotes,
  useValidateInvoice,
} from '@/hooks/useInvoices'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext'
import { creditNotesApi, invoicesApi } from '@/services/invoices'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { formatCurrency, formatDate, roundCurrencyAmount } from '@/lib/format'
import { primaryEmail } from '@/lib/clientContacts'
import { paymentsListQueryOptions, usePaymentsList } from '@/hooks/usePayments'
import { usePermissions } from '@/hooks/usePermissions'
import { cn } from '@/lib/utils'
import {
  consumeInvoiceSendIntent,
  type InvoiceSendMode,
} from '@/lib/invoiceSendIntent'
import { type CreditNote, type Invoice } from '@/types'
import recordPaymentIcon from '@/assets/invoices/record-payment.svg'

const headerCardClass =
  'rounded-2xl border border-[#e7e9ef] bg-[linear-gradient(90deg,#ffffff_0%,#fffcfa_30%,#fff8f4_65%,#fff3ec_100%)] shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'
const outlineBtnClass =
  'h-10 rounded-xl border border-[#e7e9ef] bg-white px-3 text-sm font-medium leading-none text-[#171a20] hover:bg-[#fbfbfc]'
const primaryBtnClass =
  'h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'
const recordPaymentBtnClass =
  'h-10 rounded-xl border border-[#059669] bg-white px-3 text-sm font-medium leading-none text-[#047857] hover:bg-[#ecfdf5] hover:text-[#047857]'
const iconBtnClass =
  'h-10 w-10 rounded-xl border border-transparent bg-[#FFFFFF] p-0 text-[#272C36] hover:border-[#FF5A1F] hover:bg-[#FFF1EA] hover:text-[#FF5A1F] data-[state=open]:bg-[#F4F5F8] data-[state=open]:text-[#FF5A1F] data-[state=open]:hover:bg-[#FFF1EA]'
const menuContentClass =
  'w-[240px] rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'

const menuItemClass =
  'cursor-pointer gap-3 rounded-lg px-4 py-3 text-sm font-medium leading-[1.4] text-[#171a20] focus:bg-[#f4f5f8] focus:text-[#171a20]'
const menuItemDestructiveClass =
  'cursor-pointer gap-3 rounded-lg px-4 py-3 text-sm font-medium leading-[1.4] text-[#d63b33] focus:bg-[#f4f5f8] focus:text-[#d63b33]'

// Figma 5288:127220 — tag toggle tabs in a bordered white container.
const tabListClass =
  'w-full items-start gap-1 overflow-x-auto rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)] sm:w-auto'
const tabTriggerClass =
  'gap-1 rounded-lg border-0 border-b-0 px-3 py-1.5 text-sm font-medium leading-[1.4] text-[#3d434e] hover:bg-[#f4f5f8] hover:text-[#171a20] data-[state=active]:border-b-0 data-[state=active]:bg-[#ff5a1f] data-[state=active]:text-white'
const tabBadgeClass =
  'flex h-5 min-w-5 items-center justify-center rounded-md bg-[#f4f5f8] px-1.5 py-0.5 text-xs font-medium leading-4 text-[#171a20]'

const FRACTION_DIGITS = 2

const isPresentString = (value: string | null | undefined): value is string =>
  Boolean(value)

function getClientInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
}

const metricCardBaseClass =
  'flex min-h-[96px] min-w-0 flex-1 flex-col gap-4 overflow-hidden rounded-2xl border p-4 shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'

const metricCardToneClass = {
  default: 'border-[#e7e9ef] bg-white',
  positive: 'border-[#059669] bg-[#ecfdf5]',
  brand: 'border-[#ff5a1f] bg-[#fff1ea]',
} as const

function MetricSubItems({ items }: { items: string[] }) {
  return (
    <div className='flex min-w-0 flex-wrap items-center gap-1 text-xs font-medium leading-4 text-[#5a5a5a]'>
      {items.map((item, index) => (
        <span key={`${item}-${index}`} className='contents'>
          {index > 0 && (
            <span className='size-1 shrink-0 rounded-full bg-[#5a5a5a]' />
          )}
          <span className='min-w-0'>{item}</span>
        </span>
      ))}
    </div>
  )
}

function PaymentMetricCard({
  label,
  value,
  valueClassName,
  tone = 'default',
  subItems,
  progress,
}: {
  label: string
  value: string
  valueClassName?: string
  tone?: keyof typeof metricCardToneClass
  subItems?: string[]
  progress?: {
    label: string
    percentLabel: string
    amountLabel: string
    value: number
    tone?: 'brand' | 'positive'
    icon?: 'check'
  }
}) {
  const progressTone = progress?.tone ?? 'brand'
  const progressToneClass =
    progressTone === 'positive' ? 'text-[#059669]' : 'text-[#ff5a1f]'
  const progressBarClass =
    progressTone === 'positive' ? 'bg-[#059669]' : 'bg-[#ff5a1f]'

  return (
    <div
      className={cn(
        metricCardBaseClass,
        metricCardToneClass[tone],
        'self-stretch'
      )}
    >
      <div className='flex flex-col gap-2'>
        <p className='font-heading text-xs font-medium leading-4 text-[#5a5a5a]'>
          {label}
        </p>
        <div className='flex flex-col gap-1'>
          <p
            className={cn(
              'font-heading text-[20px] font-bold leading-[1.2] tracking-[-0.02em] text-[#171a20]',
              valueClassName
            )}
          >
            {value}
          </p>
          {subItems && <MetricSubItems items={subItems} />}
          {progress && (
            <div className='flex flex-col gap-1'>
              <p
                className={cn(
                  'inline-flex items-center gap-1 text-xs font-medium leading-4',
                  progressToneClass
                )}
              >
                {progress.icon === 'check' && <Check className='size-4' />}
                {progress.label}
              </p>
              <div className='h-2 w-full overflow-hidden rounded-3xl bg-white'>
                <div
                  className={cn('h-full rounded-3xl', progressBarClass)}
                  style={{ width: `${progress.value}%` }}
                />
              </div>
              <div className='flex min-w-0 flex-wrap items-center justify-end gap-1 text-xs font-medium leading-4 text-[#6c7285]'>
                <span>{progress.percentLabel}</span>
                <span className='size-1 shrink-0 rounded-full bg-[#6c7285]' />
                <span>{progress.amountLabel}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// 2.6.4 View invoice details - tabbed (Details / Credit notes / Payments /
// History), auto-invoice indicator, payment status, status-conditional actions.
export default function InvoiceDetailPage() {
  const { t } = useTranslation(['invoices', 'quotes', 'payments'])
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  const { data, isLoading, isError } = useInvoice(id)
  const queryClient = useQueryClient()
  const updateInvoiceNotes = useUpdateInvoiceNotes(id ?? '')
  const validateInvoice = useValidateInvoice(id ?? '')
  const {
    canDelete: canDeleteModule,
    canEdit: canEditModule,
    canViewPath,
  } = usePermissions()
  useBreadcrumbLabel(data?.data?.number ?? undefined)

  const [sendOpen, setSendOpen] = useState(false)
  const [sendMode, setSendMode] = useState<InvoiceSendMode>()
  const [validateOpen, setValidateOpen] = useState(false)
  const [creditOpen, setCreditOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Invoice | null>(null)
  const [deleteCreditTarget, setDeleteCreditTarget] =
    useState<CreditNote | null>(null)
  const [sendCreditTarget, setSendCreditTarget] = useState<CreditNote | null>(
    null
  )
  const [noteDraft, setNoteDraft] = useState('')
  const [savingNotes, setSavingNotes] = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  const invoiceLoaded = !isLoading && !isError && !!data?.data
  const invoiceIdForNotes = data?.data?.id
  const creditNotesQuery = { invoice_id: id ?? '', limit: 50, page: 1 }
  const paymentsQuery = { invoice_id: id ?? '', limit: 100, page: 1 }

  const {
    data: creditNotesData,
    isLoading: creditNotesLoading,
    isError: creditNotesError,
    refetch: refetchCreditNotes,
  } = useCreditNotesList(creditNotesQuery, {
    staleTime: INVOICE_DETAIL_TAB_STALE_TIME,
    enabled:
      !!id &&
      invoiceLoaded &&
      requestedTab === 'credit-notes' &&
      data?.data?.status !== 'pro_forma' &&
      data?.data?.status !== 'pro_forma_sent',
  })
  const creditNotes = creditNotesData?.data ?? []

  // Payments linked to this invoice (invoice_id filter), for the Payments tab.
  const {
    data: paymentsData,
    isLoading: paymentsLoading,
    isError: paymentsError,
  } = usePaymentsList(paymentsQuery, {
    staleTime: INVOICE_DETAIL_TAB_STALE_TIME,
    enabled: !!id && invoiceLoaded && requestedTab === 'payments',
  })
  const payments = paymentsData?.data ?? []

  useEffect(() => {
    const sendIntent = consumeInvoiceSendIntent(searchParams)
    if (sendIntent) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setSendMode(sendIntent.mode)
      setSendOpen(true)
      /* eslint-enable react-hooks/set-state-in-effect */
      setSearchParams(sendIntent.searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (searchParams.get('export') === '1' && id) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect */
      setExportingPdf(true)
      invoicesApi
        .pdf(id)
        .then(res => {
          if (res.data?.url) {
            window.open(res.data.url, '_blank', 'noopener')
            toast({
              variant: 'success',
              title: t('shared:exportDialog.success'),
            })
          } else {
            throw new Error(t('shared:exportDialog.error'))
          }
        })
        .catch(e =>
          toast({
            variant: 'destructive',
            title: getApiErrorMessage(e, t('invoices:detail.pdfError')),
          })
        )
        .finally(() => setExportingPdf(false))
    }
  }, [searchParams, id, t])

  useEffect(() => {
    if (invoiceIdForNotes) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect */
      setNoteDraft('')
    }
  }, [invoiceIdForNotes])

  if (isLoading) {
    return (
      <div className='py-10 text-center text-muted-foreground'>
        {t('invoices:detail.loading')}
      </div>
    )
  }
  if (isError || !data?.data) {
    return (
      <div className='py-10 text-center'>
        <p className='text-destructive'>{t('invoices:detail.loadError')}</p>
        <Button
          variant='outline'
          className='mt-4'
          onClick={() => navigate('/invoices')}
        >
          {t('invoices:detail.backToInvoices')}
        </Button>
      </div>
    )
  }

  const inv = data.data
  const status = inv.status as string
  const isProForma = status === 'pro_forma' || status === 'pro_forma_sent'
  const canShowCreditNotes = !isProForma
  const isDraftSend = sendMode ? sendMode === 'draft' : isProForma
  const isValidated = status === 'validated'
  const needsPaymentActions =
    status === 'pending' ||
    status === 'partially_paid' ||
    status === 'overdue' ||
    status.startsWith('overdue_')
  const directSendDownloadView =
    status === 'paid' ||
    status === 'cancelled' ||
    status === 'cancelled_via_credit_note'
  const canMutateInvoices = canEditModule('invoices')
  const canDelete = isProForma && canDeleteModule('invoices')
  const canEditProForma = isProForma && canMutateInvoices
  const canEditNotes = canMutateInvoices
  const canValidate = isProForma && canMutateInvoices
  const canSend =
    (isValidated || needsPaymentActions || directSendDownloadView) &&
    canMutateInvoices
  const canSendProForma = isProForma && canMutateInvoices
  const canRecordPayment = needsPaymentActions && canEditModule('payments')
  const showMoreMenu = canEditProForma || canSendProForma || canDelete
  const paidPaymentTotal = roundCurrencyAmount(
    inv.paid_payment_total ?? inv.amount_paid ?? 0
  )
  const creditNoteTotal = roundCurrencyAmount(
    inv.credit_note_total ??
      creditNotes.reduce((total, creditNote) => total + creditNote.total, 0)
  )
  const balanceDue = roundCurrencyAmount(
    Math.max(inv.balance_due ?? (inv.total || 0) - (inv.amount_paid || 0), 0)
  )
  const maxCreditNoteAmount = roundCurrencyAmount(
    Math.max(
      inv.max_credit_note_amount ??
        (balanceDue > 0
          ? balanceDue
          : paidPaymentTotal > 0
            ? (inv.total || 0) - creditNoteTotal
            : 0),
      0
    )
  )
  const notesDirty = noteDraft.trim() !== ''
  const clientName =
    inv.client?.name ?? inv.client_name ?? t('invoices:detail.viewClient')
  const clientInitials = getClientInitials(clientName)
  const invoiceSubtitle = inv.title?.trim() || primaryEmail(inv.client) || ''
  const canViewClient = canViewPath(`/clients/${inv.client_id}`)
  const canViewPayment = (paymentId: string) =>
    canViewPath(`/payments/${paymentId}`)
  const fmt = (value: number) => formatCurrency(value, 'EUR', FRACTION_DIGITS)
  const totalMetricDetails = [
    t('invoices:detail.metrics.excludingVat', {
      amount: fmt(inv.subtotal),
    }),
    t('invoices:detail.metrics.vat', {
      amount: fmt(inv.tax_total),
    }),
    t('invoices:detail.metrics.services', {
      count: inv.items?.length ?? 0,
    }),
  ]
  const paymentProgress =
    inv.total > 0
      ? Math.min(
          100,
          Math.max(0, Math.round((inv.amount_paid / inv.total) * 100))
        )
      : 0
  const paymentProgressAmountLabel = t(
    'invoices:detail.metrics.paymentProgressAmount',
    {
      paid: fmt(inv.amount_paid),
      total: fmt(inv.total),
    }
  )
  const selectedPaymentId = searchParams.get('payment')
  const initialTab =
    requestedTab === 'payments' ||
    (requestedTab === 'credit-notes' && canShowCreditNotes) ||
    requestedTab === 'history'
      ? requestedTab
      : 'details'
  const preloadPayments = () => {
    if (initialTab === 'payments') return
    void queryClient.prefetchQuery(
      paymentsListQueryOptions(paymentsQuery, INVOICE_DETAIL_TAB_STALE_TIME)
    )
  }
  const preloadCreditNotes = () => {
    if (!canShowCreditNotes || initialTab === 'credit-notes') return
    void queryClient.prefetchQuery(
      creditNotesListQueryOptions(
        creditNotesQuery,
        INVOICE_DETAIL_TAB_STALE_TIME
      )
    )
  }
  const preloadHistory = () => {
    if (initialTab === 'history') return
    void queryClient.prefetchQuery(
      invoiceHistoryQueryOptions(
        inv.id,
        INVOICE_HISTORY_QUERY,
        INVOICE_DETAIL_TAB_STALE_TIME
      )
    )
  }
  const setActiveTab = (tab: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        if (tab === 'details') {
          next.delete('tab')
        } else {
          next.set('tab', tab)
        }
        return next
      },
      { replace: false }
    )
  }
  const openPaymentDrawer = (paymentId: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.set('payment', paymentId)
        return next
      },
      { replace: false }
    )
  }
  const closePaymentDrawer = () => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        next.delete('payment')
        return next
      },
      { replace: false }
    )
  }
  const lifecycleIsNeutral = status === 'cancelled_via_credit_note'
  const lifecycleIsPaid =
    status === 'paid' || (inv.total > 0 && inv.amount_paid >= inv.total)
  const balanceProgressLabel =
    creditNoteTotal > 0 && balanceDue > 0
      ? t('invoices:detail.metrics.toBeCashedInWithCreditNote', {
          amount: fmt(creditNoteTotal),
        })
      : creditNoteTotal > 0 && paidPaymentTotal > 0 && balanceDue === 0
        ? t('invoices:detail.metrics.balanceWithCreditNote', {
            amount: fmt(creditNoteTotal),
          })
        : t(
            lifecycleIsPaid
              ? 'invoices:detail.metrics.invoicePaid'
              : 'invoices:detail.metrics.toBeCashedIn'
          )
  const lifecycleStage = lifecycleIsNeutral
    ? 'neutral'
    : lifecycleIsPaid
      ? 'paid'
      : needsPaymentActions
        ? 'sent'
        : isValidated
          ? 'validated'
          : 'created'
  const lifecycleSentRecipient =
    inv.lifecycle?.sent_recipient ??
    inv.client?.email ??
    primaryEmail(inv.client)
  const lifecycleSteps: InvoiceLifecycleStep[] = [
    {
      key: 'created',
      label: t('invoices:detail.lifecycle.created'),
      detail: inv.created_at ? formatDate(inv.created_at) : null,
      state: lifecycleIsNeutral ? 'neutral' : 'complete',
    },
    {
      key: 'validated',
      label:
        lifecycleStage === 'created'
          ? t('invoices:detail.lifecycle.toValidate')
          : t('invoices:detail.lifecycle.validated'),
      detail:
        lifecycleStage !== 'created' && inv.lifecycle?.validated_at
          ? formatDate(inv.lifecycle.validated_at)
          : null,
      state: lifecycleIsNeutral
        ? 'neutral'
        : lifecycleStage === 'created'
          ? 'active'
          : 'complete',
      onClick:
        !lifecycleIsNeutral && lifecycleStage === 'created' && canValidate
          ? () => setValidateOpen(true)
          : undefined,
    },
    {
      key: 'sent',
      label:
        lifecycleStage === 'validated'
          ? t('invoices:detail.lifecycle.toSendToClient')
          : lifecycleStage === 'created'
            ? t('invoices:detail.lifecycle.sendToClient')
            : t('invoices:detail.lifecycle.sent'),
      detail:
        lifecycleStage === 'sent' || lifecycleStage === 'paid'
          ? [
              inv.sent_at ? formatDate(inv.sent_at) : null,
              lifecycleSentRecipient,
            ].filter(isPresentString)
          : null,
      state: lifecycleIsNeutral
        ? 'neutral'
        : lifecycleStage === 'created'
          ? 'upcoming'
          : lifecycleStage === 'validated'
            ? 'active'
            : 'complete',
      onClick:
        !lifecycleIsNeutral && lifecycleStage === 'validated' && canSend
          ? () => {
              setSendMode(undefined)
              setSendOpen(true)
            }
          : undefined,
    },
    {
      key: 'paid',
      label:
        lifecycleStage === 'sent'
          ? t('invoices:detail.lifecycle.toPay')
          : t('invoices:detail.lifecycle.paid'),
      detail:
        lifecycleStage === 'paid' && inv.paid_at
          ? formatDate(inv.paid_at)
          : null,
      state: lifecycleIsNeutral
        ? 'neutral'
        : lifecycleStage === 'paid'
          ? 'complete'
          : lifecycleStage === 'sent'
            ? 'active'
            : 'upcoming',
      onClick:
        !lifecycleIsNeutral && lifecycleStage === 'sent' && canRecordPayment
          ? () => navigate(`/invoices/${inv.id}?tab=payments`)
          : undefined,
    },
  ]

  const exportCreditNotePdf = async (cn: CreditNote) => {
    try {
      const res = await creditNotesApi.pdf(cn.id)
      if (!res.data?.url) throw new Error(t('shared:exportDialog.error'))
      window.open(res.data.url, '_blank', 'noopener')
      toast({ variant: 'success', title: t('shared:exportDialog.success') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('invoices:creditNotes.pdfError')),
      })
    }
  }

  const exportPdf = async () => {
    setExportingPdf(true)
    try {
      const res = await invoicesApi.pdf(inv.id)
      if (!res.data?.url) throw new Error(t('shared:exportDialog.error'))
      window.open(res.data.url, '_blank', 'noopener')
      toast({ variant: 'success', title: t('shared:exportDialog.success') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('invoices:detail.pdfError')),
      })
    } finally {
      setExportingPdf(false)
    }
  }

  const saveNotes = async () => {
    const note = noteDraft.trim()
    if (!canEditNotes || !note) return
    setSavingNotes(true)
    try {
      await updateInvoiceNotes.mutateAsync(note)
      setNoteDraft('')
      toast({
        variant: 'success',
        title: t('invoices:detail.notesSaveSuccess'),
      })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('invoices:detail.notesSaveError')),
      })
    } finally {
      setSavingNotes(false)
    }
  }

  const onValidateInvoice = async () => {
    try {
      await validateInvoice.mutateAsync()
      toast({
        variant: 'success',
        title: t('invoices:detail.validateSuccess'),
      })
      return true
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('invoices:detail.validateError')),
      })
      return false
    }
  }

  return (
    <div>
      <Tabs value={initialTab} onValueChange={setActiveTab}>
        {/* Figma 3352:105223 — invoice identity header and actions. */}
        <div className='-mx-6 -mt-6 bg-[#f4f5f8] px-6 shadow-sm'>
          <div className='flex flex-col gap-4 pb-0 pt-6'>
            <div className={cn(headerCardClass, 'p-6')}>
              <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
                <div className='flex min-w-0 flex-1 items-start gap-3'>
                  <span className='flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#ff5a1f] font-heading text-base font-semibold leading-[1.2] text-white'>
                    {clientInitials}
                  </span>
                  <div className='min-w-0 flex-1'>
                    <div className='flex flex-wrap items-center gap-2'>
                      {canViewClient ? (
                        <Link
                          to={`/clients/${inv.client_id}`}
                          className='line-clamp-1 font-heading text-xl font-semibold leading-[1.2] text-[#171a20] hover:underline'
                        >
                          {clientName}
                        </Link>
                      ) : (
                        <h1 className='line-clamp-1 font-heading text-xl font-semibold leading-[1.2] text-[#171a20]'>
                          {clientName}
                        </h1>
                      )}
                      <span className='inline-block rounded-lg bg-[#e7e9ef] px-1.5 py-1 text-xs font-medium leading-4 text-[#171a20]'>
                        {inv.number ?? t('invoices:detail.fallbackTitle')}
                      </span>
                      <InvoiceStatusBadge
                        status={inv.status}
                        dueDate={inv.due_date}
                      />
                    </div>
                    {invoiceSubtitle && (
                      <p className='mt-0.5 line-clamp-2 text-xs font-normal leading-[1.4] text-[#3d434e]'>
                        {invoiceSubtitle}
                      </p>
                    )}
                  </div>
                </div>

                <div className='flex flex-wrap items-center gap-3 lg:justify-end'>
                  <Button
                    type='button'
                    variant='outline'
                    className={outlineBtnClass}
                    disabled={exportingPdf}
                    onClick={exportPdf}
                  >
                    <Download className='mr-2 size-4' />
                    {t('invoices:detail.exportPdf')}
                  </Button>
                  {canRecordPayment && (
                    <Button
                      type='button'
                      variant='outline'
                      className={recordPaymentBtnClass}
                      onClick={() =>
                        navigate(`/invoices/${inv.id}?tab=payments`)
                      }
                    >
                      <img
                        src={recordPaymentIcon}
                        alt=''
                        className='mr-2 size-4 shrink-0'
                        aria-hidden
                      />
                      {t('invoices:detail.cashTheBill')}
                    </Button>
                  )}
                  {canValidate && (
                    <Button
                      type='button'
                      className={primaryBtnClass}
                      disabled={validateInvoice.isPending}
                      onClick={() => setValidateOpen(true)}
                    >
                      <Check className='mr-2 size-4' />
                      {t('invoices:detail.validateTheInvoice')}
                    </Button>
                  )}
                  {!canValidate && canSend && (
                    <Button
                      type='button'
                      className={primaryBtnClass}
                      onClick={() => {
                        setSendMode(undefined)
                        setSendOpen(true)
                      }}
                    >
                      <Send className='mr-2 size-4' />
                      {t('invoices:detail.sendInvoice')}
                    </Button>
                  )}
                  {showMoreMenu && (
                    <DropdownMenu>
                      <TruncatedTextTooltip
                        alwaysShow
                        closeOnPointerDown
                        className='inline-flex shrink-0'
                        content={t('invoices:detail.moreActions')}
                        delayDuration={0}
                      >
                        <DropdownMenuTrigger asChild>
                          <Button
                            type='button'
                            variant='ghost'
                            className={iconBtnClass}
                          >
                            <MoreVertical className='size-4' />
                            <span className='sr-only'>
                              {t('invoices:detail.moreActions')}
                            </span>
                          </Button>
                        </DropdownMenuTrigger>
                      </TruncatedTextTooltip>
                      <DropdownMenuContent
                        align='end'
                        className={menuContentClass}
                      >
                        {canEditProForma && (
                          <DropdownMenuItem
                            className={menuItemClass}
                            onClick={() => navigate(`/invoices/${inv.id}/edit`)}
                          >
                            <Pencil className='size-4 shrink-0' />
                            <span className='min-w-0 flex-1'>
                              {t('detail.edit')}
                            </span>
                          </DropdownMenuItem>
                        )}
                        {canSendProForma && (
                          <DropdownMenuItem
                            className={menuItemClass}
                            onClick={() => {
                              setSendMode('draft')
                              setSendOpen(true)
                            }}
                          >
                            <Send className='size-4 shrink-0' />
                            <span className='min-w-0 flex-1'>
                              {t('invoices:detail.sendInvoice')}
                            </span>
                          </DropdownMenuItem>
                        )}
                        {canSendProForma && canDelete && (
                          <DropdownMenuSeparator className='mx-1 my-2 bg-[#e7e9ef]' />
                        )}
                        {canDelete && (
                          <DropdownMenuItem
                            className={menuItemDestructiveClass}
                            onClick={() => setDeleteTarget(inv)}
                          >
                            <Trash2 className='size-4 shrink-0' />
                            <span className='min-w-0 flex-1'>
                              {t('invoices:detail.deleteTheInvoice')}
                            </span>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            </div>

            <div
              className={cn(
                'flex flex-col gap-4 sm:flex-row sm:items-stretch',
                isProForma && 'sm:max-w-[578px]'
              )}
            >
              <PaymentMetricCard
                label={t('invoices:detail.totalAmount')}
                value={fmt(inv.total)}
                subItems={totalMetricDetails}
              />
              {!isProForma && (
                <>
                  <PaymentMetricCard
                    label={t('invoices:detail.amountPaid')}
                    value={fmt(paidPaymentTotal)}
                    tone='positive'
                    valueClassName='text-[#059669]'
                  />
                  <PaymentMetricCard
                    label={t('invoices:detail.balanceDue')}
                    value={fmt(balanceDue)}
                    tone={lifecycleIsPaid ? 'positive' : 'brand'}
                    valueClassName={
                      lifecycleIsPaid ? 'text-[#059669]' : 'text-[#ff5a1f]'
                    }
                    progress={{
                      label: balanceProgressLabel,
                      percentLabel: t(
                        'invoices:detail.metrics.cashedOutPercent',
                        {
                          percent: paymentProgress,
                        }
                      ),
                      amountLabel: paymentProgressAmountLabel,
                      value: paymentProgress,
                      tone: lifecycleIsPaid ? 'positive' : 'brand',
                      icon: lifecycleIsPaid ? 'check' : undefined,
                    }}
                  />
                </>
              )}
            </div>

            <TabsList className={tabListClass}>
              <TabsTrigger value='details' className={tabTriggerClass}>
                {t('invoices:detail.tabs.details')}
              </TabsTrigger>
              <TabsTrigger
                value='payments'
                className={tabTriggerClass}
                onPointerEnter={preloadPayments}
                onFocus={preloadPayments}
              >
                <span>{t('invoices:detail.tabs.payments')}</span>
                <span className={tabBadgeClass}>{inv.payments_count}</span>
              </TabsTrigger>
              <TabsTrigger
                value='history'
                className={tabTriggerClass}
                onPointerEnter={preloadHistory}
                onFocus={preloadHistory}
              >
                {t('invoices:detail.tabs.history')}
              </TabsTrigger>
              {canShowCreditNotes && (
                <TabsTrigger
                  value='credit-notes'
                  className={tabTriggerClass}
                  onPointerEnter={preloadCreditNotes}
                  onFocus={preloadCreditNotes}
                >
                  {t('invoices:detail.tabs.creditNotes')}
                </TabsTrigger>
              )}
            </TabsList>
          </div>
        </div>

        {/* Figma 1085:12754 — tab content on grey background */}
        <div className='-mx-6 flex flex-col gap-4 bg-[#fafafa] px-6 py-6'>
          <TabsContent value='details' className='mt-0'>
            <InvoiceDetailsTab
              invoice={inv}
              canEditNotes={canEditNotes}
              noteDraft={noteDraft}
              notesDirty={notesDirty}
              savingNotes={savingNotes}
              lifecycleSteps={lifecycleSteps}
              lifecycleIsNeutral={lifecycleIsNeutral}
              onNotesChange={setNoteDraft}
              onSaveNotes={saveNotes}
              onOpenHistory={() => navigate(`/invoices/${inv.id}?tab=history`)}
            />
          </TabsContent>

          {canShowCreditNotes && (
            <TabsContent value='credit-notes' className='mt-0'>
              <InvoiceCreditNotesTab
                creditNotes={creditNotes}
                isLoading={creditNotesLoading}
                isError={creditNotesError}
                canMutateInvoices={canMutateInvoices}
                canCreateCreditNote={
                  canMutateInvoices && maxCreditNoteAmount > 0
                }
                onCreateCreditNote={() => setCreditOpen(true)}
                onSendCreditNote={setSendCreditTarget}
                onExportCreditNote={exportCreditNotePdf}
                onDeleteCreditNote={setDeleteCreditTarget}
              />
            </TabsContent>
          )}

          <TabsContent value='payments' className='mt-0'>
            <InvoicePaymentsTab
              invoice={inv}
              payments={payments}
              isLoading={paymentsLoading}
              isError={paymentsError}
              canEditPayments={canEditModule('payments')}
              canViewPayment={canViewPayment}
              onOpenPayment={openPaymentDrawer}
            />
          </TabsContent>

          <TabsContent value='history' className='mt-0'>
            <InvoiceHistoryTab invoice={inv} />
          </TabsContent>
        </div>
      </Tabs>

      <PaymentDrawerShell
        open={!!selectedPaymentId}
        title={t('payments:detail.drawerTitle')}
        onClose={closePaymentDrawer}
      >
        {selectedPaymentId ? (
          <PaymentDetailDrawerContent
            key={selectedPaymentId}
            paymentId={selectedPaymentId}
            onClose={closePaymentDrawer}
          />
        ) : null}
      </PaymentDrawerShell>

      <InvoiceValidateDialog
        open={validateOpen}
        busy={validateInvoice.isPending}
        invoiceNumber={inv.number ?? inv.id.slice(0, 8)}
        onOpenChange={setValidateOpen}
        onConfirm={async () => {
          const validated = await onValidateInvoice()
          if (validated) setValidateOpen(false)
        }}
      />

      <InvoiceSendDialog
        open={sendOpen}
        onOpenChange={open => {
          setSendOpen(open)
          if (!open) setSendMode(undefined)
        }}
        title={t(
          isDraftSend
            ? 'invoices:detail.sendDraftTitle'
            : 'invoices:detail.sendTitle'
        )}
        defaultTo={inv.client?.email ?? primaryEmail(inv.client)}
        defaultSubject={t(
          isDraftSend
            ? 'invoices:detail.sendDraftSubject'
            : 'invoices:detail.sendSubject',
          {
            number: inv.number ?? '',
          }
        ).trim()}
        clientName={
          inv.client?.name ??
          inv.client_name ??
          t('invoices:detail.invoiceSendDialog.unknownClient')
        }
        reference={inv.number ?? inv.id.slice(0, 8)}
        totalLabel={formatCurrency(inv.total, 'EUR', FRACTION_DIGITS)}
        successMessage={t(
          isDraftSend
            ? 'invoices:detail.sendDraftSuccess'
            : 'invoices:detail.sendSuccess'
        )}
        errorMessage={t('invoices:detail.sendError')}
        onSend={async body => {
          await invoicesApi.send(inv.id, {
            recipient_email: body.recipient_email,
            message: body.message || undefined,
            include_bank_details: body.include_bank_details,
            mode: sendMode,
          })
          queryClient.invalidateQueries({
            queryKey: ['invoices', 'detail', inv.id],
          })
          queryClient.invalidateQueries({ queryKey: ['invoices', 'list'] })
          queryClient.invalidateQueries({ queryKey: ['invoices', 'summary'] })
          invalidatePortalMetricQueries(queryClient)
        }}
      />

      <SendCreditNoteDialog
        creditNote={sendCreditTarget}
        fallbackClient={inv.client}
        onClose={() => setSendCreditTarget(null)}
        onSent={() => refetchCreditNotes()}
      />

      <CreateCreditNoteDialog
        open={creditOpen}
        onOpenChange={setCreditOpen}
        invoiceId={inv.id}
        clientId={inv.client_id}
        clientName={inv.client?.name ?? inv.client_name}
        invoiceNumber={inv.number}
        balanceDue={balanceDue}
        paidAmount={paidPaymentTotal}
        maxCreditAmount={maxCreditNoteAmount}
        existingCreditNotes={creditNotes}
        onCreated={() => refetchCreditNotes()}
      />

      <DeleteInvoiceDialog
        invoice={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        navigateOnDelete
      />
      <DeleteCreditNoteDialog
        creditNote={deleteCreditTarget}
        onClose={() => setDeleteCreditTarget(null)}
        onDeleted={() => {
          setDeleteCreditTarget(null)
          refetchCreditNotes()
        }}
      />
      <LoadingOverlay open={exportingPdf} />
    </div>
  )
}
